import { Box, Divider, Grid, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import TaxSummaryHover from '../../../common/Tax/TaxSummaryHover';
import { computeLabourPartsFromServices } from '../../../utility/helpers/computeLabourPartsFromServices';
import { formatEstimateDiscountDetailDisplay } from '../../../utility/helpers/discountCalculation';
import { NumericEngine } from '../../../utility/helpers/numericEngine';
import { DOCUMENT_TAX_TYPE, formatTaxPercent, isNewTaxVersion, summarizeTaxFromRecord } from '../../../utility/helpers/taxCalculation';
import { useTaxDecimalPrecision } from '../../../utility/hooks/useTaxDecimalPrecision';

const formatSummaryCurrency = (n) => (Number(n) > 0 ? NumericEngine.format(n, { formatCurrency: true, trailingZeros: true }) : '$0.00');

const EstimatePaymentDetails = ({ estimateData = {} }) => {
    const taxPrecision = useTaxDecimalPrecision();
    const { labourTotal, partsTotal, packagesTotal } = useMemo(() => computeLabourPartsFromServices(estimateData?.services), [estimateData?.services]);

    // Show the STORED per-tax name/rate/amount from the GET response (calculation is authoritative
    // server-side). Backend top-level rows carry taxName + taxPercentage + taxAmount.
    const appliedTaxLines = useMemo(() => {
        if (!isNewTaxVersion(estimateData)) return [];
        return summarizeTaxFromRecord(estimateData, DOCUMENT_TAX_TYPE.ESTIMATE).filter((row) => row.applyTax && Number(row.taxAmount) > 0);
    }, [estimateData]);

    // Sum of the per-tax amounts shown above — matches the invoice tax total.
    const totalTax = appliedTaxLines?.length > 0 ? appliedTaxLines.reduce((acc, r) => NumericEngine.add(acc, String(r.taxAmount || 0)), '0') : 0;

    return (
        <Grid container justifyContent={'space-between'} padding={'0 16px 24px 24px'}>
            <Stack direction={'row'} width={'100%'} justifyContent={'space-between'} alignItems={'center'} gap={2} mb={1}>
                <Typography>Labour</Typography>
                <Typography>{formatSummaryCurrency(labourTotal)}</Typography>
            </Stack>
            <Stack direction={'row'} width={'100%'} justifyContent={'space-between'} alignItems={'center'} gap={2} mb={1}>
                <Typography>Parts</Typography>
                <Typography>{formatSummaryCurrency(partsTotal)}</Typography>
            </Stack>
            <Stack direction={'row'} width={'100%'} justifyContent={'space-between'} alignItems={'center'} gap={2} mb={1}>
                <Typography>Service Packages</Typography>
                <Typography>{formatSummaryCurrency(packagesTotal)}</Typography>
            </Stack>
            <Divider sx={{ backgroundColor: '#0000001F', margin: '8px 0', width: '100%' }} />
            <Stack direction={'row'} width={'100%'} justifyContent={'space-between'} alignItems={'center'} gap={2} mb={1}>
                <Typography>Subtotal</Typography>
                <Typography>
                    {estimateData?.subTotal ? NumericEngine.format(estimateData?.subTotal || 0, { formatCurrency: true, trailingZeros: true }) : '$0.00'}
                </Typography>
            </Stack>
            {estimateData?.discount > 0 && (
                <Stack direction={'row'} width={'100%'} justifyContent={'space-between'} alignItems={'center'} gap={2} mb={1}>
                    <Box>
                        {estimateData?.membershipProgram?.programName
                            ? `Discount - ${estimateData?.membershipProgram?.programName} (${estimateData?.membershipProgram?.discount}%)`
                            : 'Discount'}
                    </Box>
                    <Typography>
                        {formatEstimateDiscountDetailDisplay({
                            discount: estimateData?.discount,
                            discountPercentage: estimateData?.discountPercentage,
                            discountSource: estimateData?.discountSource,
                            subTotal: estimateData?.subTotal,
                            membershipProgram: estimateData?.membershipProgram,
                        })}
                    </Typography>
                </Stack>
            )}
            {estimateData?.totalFeesAmount > 0 && (
                <Stack direction={'row'} width={'100%'} justifyContent={'space-between'} alignItems={'center'} gap={2} mb={1}>
                    <Typography>Fees</Typography>

                    <Typography>
                        {estimateData?.totalFeesAmount
                            ? NumericEngine.format(estimateData.totalFeesAmount, { formatCurrency: true, trailingZeros: true })
                            : '$0.00'}
                    </Typography>
                </Stack>
            )}
            {isNewTaxVersion(estimateData) ? (
                appliedTaxLines.map((row) => (
                    <Stack
                        key={row.taxId || row.name}
                        direction={'row'}
                        width={'100%'}
                        justifyContent={'space-between'}
                        alignItems={'center'}
                        gap={2}
                        mb={'8px'}
                    >
                        <TaxSummaryHover summary={row}>
                            <Typography>
                                {row.name} ({formatTaxPercent(row.rate, taxPrecision)}%)
                            </Typography>
                        </TaxSummaryHover>
                        {/* Amount is a second, independent trigger for the same card — same as the
                            editable summary, where the name and the amount each open it. */}
                        <TaxSummaryHover summary={row}>
                            <Typography>
                                {Number(row.taxAmount) > 0 ? NumericEngine.format(row.taxAmount, { formatCurrency: true, trailingZeros: true }) : '$0.00'}
                            </Typography>
                        </TaxSummaryHover>
                    </Stack>
                ))
            ) : (
                <Stack direction={'row'} width={'100%'} justifyContent={'space-between'} alignItems={'center'} gap={2} mb={0}>
                    <Typography>Tax</Typography>
                    <Typography>
                        {estimateData?.tax ? NumericEngine.format(estimateData.tax, { formatCurrency: true, trailingZeros: true }) : '$0.00'}
                    </Typography>
                </Stack>
            )}
            {/* Total tax line  */}
            {isNewTaxVersion(estimateData) && appliedTaxLines?.length > 1 && (
                <Stack direction={'row'} width={'100%'} justifyContent={'space-between'} alignItems={'center'} gap={2} mb={'8px'}>
                    <Typography sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>Total Tax</Typography>
                    <Typography sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>{formatSummaryCurrency(totalTax)}</Typography>
                </Stack>
            )}

            <Divider sx={{ backgroundColor: '#0000001F', margin: '4px 0 12px 0', width: '100%' }} />

            <Grid item xs={6}>
                <Typography fontWeight={600} sx={{ fontSize: '18px' }}>
                    Total
                </Typography>
            </Grid>
            <Grid item xs={6} textAlign={'end'}>
                <Typography fontWeight={600} sx={{ fontSize: '18px' }}>
                    {estimateData?.Total ? NumericEngine.format(estimateData.Total, { formatCurrency: true, trailingZeros: true }) : '$0.00'}
                </Typography>
            </Grid>
        </Grid>
    );
};

export default EstimatePaymentDetails;
